import React from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { WintSuperkollAPIModelAnnualAccounts } from "wint-api-client/dist/types";
import { ChartEnums } from "models/enums";
import { TranslationKeys } from "translations";
import { TimelineItem } from "models/propTypes/chartProps";
//#endregion
//#region components
import { SimpleTimeline } from "components/common";
import { useAnnualAccountService } from "serviceHooks";
import AnnualReportHighlightedStep from "./highlightedStep";
import { ViewPanelContentSkeleton } from "components/common/skeletons";
import useDueWeekText from "./useDueWeekText";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";

const AnnualReportTimeline: React.FC = (props) => {
  const annualAccountsService = useAnnualAccountService();

  const annualAccountsFinancialYear = useSelector(
    (store: AppState) => store.auth.selectedCompany.annualAccountsFinancialYear
  );

  const [timeline, setTimeline] = React.useState<WintSuperkollAPIModelAnnualAccounts.AnnualReportTimeLine>();

  React.useEffect(() => {
    if (annualAccountsFinancialYear?.id) {
      annualAccountsService
        .getAnnualReportTimeline(annualAccountsFinancialYear.id)
        .then((res) => setTimeline(res.data));
    } else {
      setTimeline(undefined);
    }
  }, [annualAccountsFinancialYear]);

  const getResponsibleText = (
    responsible: WintSuperkollAPIModelAnnualAccounts.AnnualReportTimeLineStepResponsiblePerson
  ) => {
    return LocalizationHelperInstance.getTranslatedEnumValue(
      "Accounting.TimeLineStepResponsiblePerson",
      WintSuperkollAPIModelAnnualAccounts.AnnualReportTimeLineStepResponsiblePerson[responsible].toString()
    );
  };

  const steps = timeline?.steps ?? [];
  const activeStepIndex = timeline?.activeStepIndex ?? -1;
  const currentStep = activeStepIndex > -1 ? steps[activeStepIndex] : undefined;
  const isCurrentStepLast = activeStepIndex + 1 === steps.length;
  const stepsBefore = activeStepIndex > -1 ? steps.slice(0, activeStepIndex) : undefined;
  const stepsAfter = activeStepIndex > -1 ? steps.slice(activeStepIndex + 1) : steps;

  const getDueWeekText = useDueWeekText();

  const getSubTexts = (step: WintSuperkollAPIModelAnnualAccounts.AnnualReportTimeLineStep) => {
    const subTexts = [];

    let stateText: string | undefined;
    switch (step.state) {
      case WintSuperkollAPIModelAnnualAccounts.AnnualReportTimeLineStepState.NotStarted:
        stateText = getResponsibleText(step.responsible);
        break;
      case WintSuperkollAPIModelAnnualAccounts.AnnualReportTimeLineStepState.Ongoing:
        stateText = step.stateDescription;
        break;
      case WintSuperkollAPIModelAnnualAccounts.AnnualReportTimeLineStepState.Delayed:
        stateText = LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Delayed);
        break;
    }

    if (stateText) {
      subTexts.push(stateText);
    }

    const dueText = getDueWeekText(step);
    if (dueText) {
      subTexts.push(dueText);
    }

    return subTexts;
  };

  const getTooltip = (step: WintSuperkollAPIModelAnnualAccounts.AnnualReportTimeLineStep) => {
    if (step.dueDateDescription) {
      return step.dueDateDescription;
    }

    switch (step.state) {
      case WintSuperkollAPIModelAnnualAccounts.AnnualReportTimeLineStepState.Delayed:
      case WintSuperkollAPIModelAnnualAccounts.AnnualReportTimeLineStepState.Done:
        return step.stateDescription;
      default:
        return undefined;
    }
  };

  const getItemMapping = (steps: WintSuperkollAPIModelAnnualAccounts.AnnualReportTimeLineStep[]) =>
    steps.map(
      (step): TimelineItem => ({
        text: step.title ?? "",
        subText: getSubTexts(step),
        type: step.state as unknown as ChartEnums.TimelineStepType,
        toolTip: getTooltip(step),
      })
    ) ?? [];

  return timeline === undefined ? (
    <ViewPanelContentSkeleton />
  ) : (
    <>
      {stepsBefore ? <SimpleTimeline items={getItemMapping(stepsBefore)} /> : <></>}
      <AnnualReportHighlightedStep currentStep={currentStep} isCurrentStepLast={isCurrentStepLast} />
      {stepsAfter ? <SimpleTimeline items={getItemMapping(stepsAfter)} /> : <></>}
    </>
  );
};

export default AnnualReportTimeline;
